import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { type ScrapedUser } from "@/lib/scraper";
import { useChannels } from "@/lib/channels";
import { DetailDrawer } from "@/components/ui/drawer";
import { type TabItem } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatRelative } from "@/lib/format";
import { LastOnline } from "@/components/audience/last-online";
import { userDisplayName } from "@/lib/audience";
import { UserFlags } from "@/components/audience/UserFlags";

/**
 * UserDetailDrawer — Audience user detail as a DetailDrawer over the list
 * (local state, no route). Shows the profile + the TG signals the audit asked
 * for: phone, first_seen channel, activity_score, last_online.
 */
export function UserDetailDrawer({
  user,
  open,
  onOpenChange,
}: {
  user: ScrapedUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const channels = useChannels();

  if (!user) return null;

  const display = userDisplayName(user);
  const fullName =
    [user.first_name, user.last_name].filter(Boolean).join(" ") || "—";

  const channelById = new Map((channels.data ?? []).map((c) => [c.id, c] as const));
  const labelOf = (id: string): string => {
    const c = channelById.get(id);
    return c
      ? c.username
        ? `@${c.username}`
        : (c.title ?? String(c.tg_channel_id ?? c.id))
      : id.slice(0, 8);
  };
  // ALL channels the user was scraped from; fall back to first_seen for old rows.
  const channelIds =
    user.channel_ids && user.channel_ids.length > 0
      ? user.channel_ids
      : user.first_seen_channel_id
        ? [user.first_seen_channel_id]
        : [];

  const profileRows: Row[] = [
    {
      label: t("users_redesign.drawer.tg_user_id"),
      value: <span className="font-mono text-sm">{user.tg_user_id}</span>,
    },
    {
      label: t("users_redesign.drawer.username"),
      value: user.username ? (
        <span className="font-mono">@{user.username}</span>
      ) : (
        "—"
      ),
    },
    { label: t("users_redesign.drawer.name"), value: fullName },
    {
      label: t("users_redesign.drawer.phone"),
      value: user.phone ? (
        <span className="font-mono text-sm">{user.phone}</span>
      ) : (
        <span className="text-fg-muted">
          {t("users_redesign.drawer.phone_none")}
        </span>
      ),
    },
    {
      label: t("users_redesign.drawer.language"),
      value: user.language_code ?? "—",
    },
    { label: t("users_redesign.drawer.flags"), value: <UserFlags user={user} /> },
  ];

  const signalRows: Row[] = [
    // Imported people have no source channel at all, so their provenance is
    // the note taken at import time — without it the row looks like it came
    // from nowhere.
    ...(user.import_label
      ? [
          {
            label: t("users_redesign.drawer.imported_from"),
            value: (
              <Badge tone="neutral" variant="subtle" className="px-2 py-0.5">
                {user.import_label}
              </Badge>
            ),
          },
        ]
      : []),
    {
      label: t("users_redesign.drawer.channels"),
      value:
        channelIds.length === 0 ? (
          <span className="text-fg-muted">
            {t("users_redesign.drawer.first_seen_channel_none")}
          </span>
        ) : (
          <div className="flex flex-wrap justify-end gap-1">
            {channelIds.map((id) => (
              <Badge key={id} tone="accent" variant="subtle" className="px-2 py-0.5">
                {labelOf(id)}
              </Badge>
            ))}
          </div>
        ),
    },
    {
      label: t("users_redesign.drawer.activity_score"),
      value:
        user.activity_score != null ? (
          <span className="font-mono text-sm">{user.activity_score}</span>
        ) : (
          "—"
        ),
    },
    {
      label: t("users_redesign.drawer.last_online"),
      value: <LastOnline user={user} />,
    },
    {
      label: t("users_redesign.drawer.scraped_at"),
      value: formatRelative(user.scraped_at),
    },
  ];

  const tabs: TabItem[] = [
    {
      value: "overview",
      label: t("users.col.user"),
      content: (
        <div className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <h3 className="mb-3 text-sm font-semibold text-fg-primary">
                {t("users_redesign.drawer.section_profile")}
              </h3>
              <DefList rows={profileRows} />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <h3 className="mb-3 text-sm font-semibold text-fg-primary">
                {t("users_redesign.drawer.section_signals")}
              </h3>
              <DefList rows={signalRows} />
            </CardContent>
          </Card>
        </div>
      ),
    },
  ];

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={display}
      breadcrumbs={[
        { label: t("users_redesign.drawer.breadcrumb") },
        { label: display },
      ]}
      tabs={tabs}
      size="md"
    />
  );
}

interface Row {
  label: string;
  value: ReactNode;
}

function DefList({ rows }: { rows: Row[] }) {
  return (
    <dl className="divide-y divide-border">
      {rows.map((r) => (
        <div
          key={r.label}
          className="flex items-center justify-between gap-3 py-2 text-sm"
        >
          <dt className="text-fg-muted">{r.label}</dt>
          <dd className="text-right text-fg-primary">{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}
